import type { CheckoutOrderLineInput } from "./checkoutOrderTotals";

/** Subset of Product fields needed to mirror inventory decrement rules in StripeService */
export type ProductInventoryLean = {
  inventoryQuantity?: number;
  variants?: Array<{
    _id: unknown;
    quantity?: number;
  }>;
};

type ProductInventoryVariant = NonNullable<
  ProductInventoryLean["variants"]
>[number];

export function variantUsesSharedPool({
  variant,
}: {
  variant: ProductInventoryVariant;
}): boolean {
  return typeof variant.quantity !== "number";
}

export function resolveLineItemStock({
  product,
  variantId,
}: {
  product: ProductInventoryLean;
  variantId?: string;
}): number | undefined {
  const vid = variantId?.trim();
  if (vid && product.variants?.length) {
    const variant = product.variants.find(
      (x) => String((x as { _id: unknown })._id) === vid
    );
    if (variant) {
      if (typeof variant.quantity === "number") {
        return variant.quantity;
      }
    }
  }
  if (typeof product.inventoryQuantity === "number") {
    return product.inventoryQuantity;
  }
  return undefined;
}

export function isSoldOut({ stock }: { stock: number | undefined }): boolean {
  return stock === 0;
}

export function variantIsPurchasable({
  variant,
  inventoryQuantity,
}: {
  variant: ProductInventoryVariant;
  inventoryQuantity?: number;
}): boolean {
  return !isSoldOut({
    stock: resolveLineItemStock({
      product: { inventoryQuantity, variants: [variant] },
      variantId: String((variant as { _id: unknown })._id),
    }),
  });
}

/**
 * Returns an error message when any purchased line exceeds tracked stock:
 * — per-variant `variants[].quantity` when set on the variant
 * — otherwise product-level {@link ProductInventoryLean.inventoryQuantity} shared pool
 */
export function inventoryValidationErrorForCheckoutLines({
  lineItemsInput,
  productsById,
}: {
  lineItemsInput: CheckoutOrderLineInput[];
  productsById: Map<string, ProductInventoryLean>;
}): string | null {
  const variantDemand = new Map<string, number>();
  const sharedPoolDemand = new Map<string, number>();

  for (const li of lineItemsInput) {
    if (li.quantity <= 0) continue;
    const product = productsById.get(li.productId);
    if (!product) continue;

    const variantId = li.variantId?.trim();
    const variant =
      variantId && product.variants?.length
        ? product.variants.find(
            (x) => String((x as { _id: unknown })._id) === variantId
          )
        : undefined;

    if (variant && !variantUsesSharedPool({ variant })) {
      const key = `${li.productId}:${variantId}`;
      variantDemand.set(key, (variantDemand.get(key) ?? 0) + li.quantity);
      continue;
    }

    if (typeof product.inventoryQuantity === "number") {
      sharedPoolDemand.set(
        li.productId,
        (sharedPoolDemand.get(li.productId) ?? 0) + li.quantity
      );
    }
  }

  for (const [key, demand] of variantDemand) {
    const [productId, variantId] = key.split(":");
    const product = productsById.get(productId);
    if (!product) continue;
    const variant = product.variants?.find(
      (x) => String((x as { _id: unknown })._id) === variantId
    );
    if (
      variant &&
      typeof variant.quantity === "number" &&
      variant.quantity < demand
    ) {
      return "Product variant is out of stock";
    }
  }

  for (const [productId, demand] of sharedPoolDemand) {
    const product = productsById.get(productId);
    if (
      product &&
      typeof product.inventoryQuantity === "number" &&
      product.inventoryQuantity < demand
    ) {
      return "Product is out of stock";
    }
  }

  return null;
}
